import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LogsService } from './logs.service';

@Injectable({ providedIn: 'root' })
export class ErrorLoggingService {
  private static globalHandlersInitialized = false;

  constructor(private logsService: LogsService) {
    this.initGlobalErrorHandlers();
  }

  logError(
    error: unknown,
    source: 'http' | 'script' | 'promise' | 'angular',
    extra?: Record<string, unknown>
  ): void {
    const message = this.formatError(error, source, extra);
    if (!message) {
      return;
    }

    this.logsService
      .setLogs(message)
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  private initGlobalErrorHandlers(): void {
    if (ErrorLoggingService.globalHandlersInitialized) {
      return;
    }

    ErrorLoggingService.globalHandlersInitialized = true;

    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('error', (event: ErrorEvent) => {
      const error = event.error || event.message || 'Unknown script error';
      this.logError(error, 'script', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      this.logError(event.reason ?? 'Unhandled promise rejection', 'promise');
    });
  }

  private formatError(error: unknown, source: string, extra?: Record<string, unknown>): string {
    const timestamp = new Date().toISOString();
    const base: Record<string, unknown> = {
      source,
      timestamp,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      ...extra,
    };

    if (error instanceof HttpErrorResponse) {
      return JSON.stringify({
        ...base,
        type: 'HttpErrorResponse',
        status: error.status,
        statusText: error.statusText,
        message: error.message,
        requestUrl: error.url,
        error: error.error,
      });
    }

    if (error instanceof Error) {
      return JSON.stringify({
        ...base,
        type: error.name,
        message: error.message,
        stack: error.stack,
      });
    }

    if (typeof error === 'string') {
      return JSON.stringify({
        ...base,
        type: 'StringError',
        message: error,
      });
    }

    try {
      return JSON.stringify({
        ...base,
        type: 'UnknownError',
        error,
      });
    } catch {
      return JSON.stringify({
        ...base,
        type: 'UnknownError',
        message: 'Unserializable error',
      });
    }
  }
}
